import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import { fetchPostIncentives, fetchIncentiveCommissions, fetchTravelCommissions, fetchCarCommissions, fetchHouseCommissions, fetchLifestyleCommissions } from "../script/rpc";
import PageLoader from "./PageLoader";

/* ─────────────────────────────────────────────────────────────
   HELPERS & FORMATTING
───────────────────────────────────────────────────────────── */
const fmtAmt = (val) =>
  val == null || val === false
    ? "—"
    : new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);

const fmtRange = (val) =>
  val == null || val === false
    ? "—"
    : new Intl.NumberFormat("en-IN").format(val);

const fmtPct = (val) => (val == null || val === false ? "0.00" : Number(val).toFixed(2)) + "%";

/* ══════════════════════════════════════════════════════
   REUSABLE: Reward / Commission table
   Renders as a table on desktop, stacked cards on mobile.
   `columns` drives both render paths so there's one source
   of truth per table instead of 6 hand-duplicated blocks.
══════════════════════════════════════════════════════ */
function RewardsTable({ rows, columns, emptyLabel, rowKey = (r) => r.id }) {
  return (
    <div style={S.listContainer}>
      <div style={S.tableCard}>
        {/* Desktop / tablet table */}
        <div className="rt-table-wrap">
          <table style={S.table} className="rt-table">
            <thead>
              <tr style={S.theadTr}>
                {columns.map((col, i) => (
                  <th
                    key={col.key}
                    style={{ ...S.th, textAlign: col.align === "right" ? "right" : "left" }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={rowKey(row)} style={S.tr}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      style={{ ...S.td, textAlign: col.align === "right" ? "right" : "left" }}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
                    {emptyLabel}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="rt-card-list">
          {rows.length === 0 ? (
            <div style={{ padding: "40px 20px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              {emptyLabel}
            </div>
          ) : (
            rows.map((row) => (
              <div key={rowKey(row)} className="rt-card">
                {columns.map((col) => (
                  <div key={col.key} className="rt-card-row">
                    <span className="rt-card-label">{col.label}</span>
                    <span className="rt-card-value">{col.render(row)}</span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════ */
export default function PostIncentivesPage() {
  const { auth } = useAuth();
  const { uid, password } = auth;
  const [loading, setLoading] = useState(true);
  const [incentives, setIncentives] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [travelCommissions, setTravelCommissions] = useState([]);
  const [houseCommissions, setHouseCommissions] = useState([]);
  const [carCommissions, setCarCommissions] = useState([]);
  const [lifestyleCommissions, setLifestyleCommissions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [incData, commData, travelData, carData, houseData, lifestyleData] = await Promise.all([
          fetchPostIncentives(uid, password),
          fetchIncentiveCommissions(uid, password),
          fetchTravelCommissions(uid, password),
          fetchCarCommissions(uid, password),
          fetchHouseCommissions(uid, password),
          fetchLifestyleCommissions(uid, password)
        ]);
        if (alive) {
          setIncentives(incData);
          setCommissions(commData);
          setTravelCommissions(travelData);
          setCarCommissions(carData);
          setHouseCommissions(houseData);
          setLifestyleCommissions(lifestyleData);
        }
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [uid, password]);

  if (loading) {
    return (
      <main className="dp-main" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", borderWidth: "4px", borderStyle: "solid", borderColor: "#e2e8f0", borderTopColor: "#7c3aed", animation: "dp-spin 0.8s linear infinite", marginBottom: 16 }} />
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#64748b" }}>Loading incentives configuration...</p>
      </main>
    );
  }

  // Column definitions for the "standard" 5-column reward tables
  // (Travel / Car / House / Lifestyle all share this exact shape)
  const rewardColumns = [
    { key: "serial", label: "SERIAL NUMBER", render: (r) => (
      <span style={{ ...S.rankName, fontSize: "14px" }}>{r.serial_number || "—"}</span>
    ) },
    { key: "rank", label: "RANK", render: (r) => (
      <span style={{ ...S.rankName, fontSize: "14px" }}>{r.name || r.post_id?.[1] || "—"}</span>
    ) },
    { key: "commission", label: "COMMISSION", render: (r) => (
      <span style={{ ...S.commissionVal, fontSize: "14px" }}>{fmtPct(r.commission_percentage)}</span>
    ) },
    { key: "legs", label: "LEGS", render: (r) => (
      <span style={{ ...S.legCount, fontSize: "14px" }}>{r.min_legs} Legs</span>
    ) },
    { key: "budget", label: "MIN BUDGET", align: "right", render: (r) => (
      <span style={{ fontWeight: "700", color: "#10b981" }}>{fmtAmt(r.min_budget)}</span>
    ) },
  ];

  return (
    <main className="dp-main" style={{ ...S.root, position: "relative" }}>
      <style>{GLOBAL_CSS}</style>

      {/* Modern Glassy Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Rank & Rewards</h1>
          <p style={S.subtitle}>Post incentives</p>
        </div>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {/* Post Incentives Table */}
      <RewardsTable
        rows={incentives}
        emptyLabel="No post incentives found."
        columns={[
          { key: "rank", label: "RANK TIERS", render: (inc) => (
            <div style={S.rankInfo}>
              <div style={S.rankIcon}>#{inc.serial_number}</div>
              <span style={S.rankName}>{inc.name}</span>
            </div>
          ) },
          { key: "commission", label: "COMMISSION", render: (inc) => (
            <div style={S.commissionBadge}>
              <span style={S.commissionVal}>{fmtPct(inc.commission_percentage)}</span>
            </div>
          ) },
          { key: "legs", label: "LEGS", render: (inc) => (
            <div style={S.structureInfo}>
              <span style={S.legCount}>{inc.min_legs || 0}</span>
            </div>
          ) },
          { key: "budget", label: "BUSINESS BUDGET", align: "right", render: (inc) => (
            <div style={S.budgetInfo}>
              <span style={S.budgetVal}>{fmtAmt(inc.min_budget)}</span>
            </div>
          ) },
        ]}
      />

      {/* Commission Configuration Table */}
      <div style={{ marginTop: "48px" }}>
        <h2 style={{ ...S.title, marginBottom: "20px" }}>Commission Configuration</h2>
        <RewardsTable
          rows={commissions}
          emptyLabel="No commission configuration found."
          columns={[
            { key: "serial", label: "SERIAL NUMBER", render: (c) => (
              <span style={{ ...S.rankName, fontSize: "14px" }}>{c.serial_number || "—"}</span>
            ) },
            { key: "rank", label: "RANK", render: (c) => (
              <span style={{ ...S.rankName, fontSize: "14px" }}>{c.post_id?.[1] || "—"}</span>
            ) },
            { key: "level", label: "LEVEL", render: (c) => (
              <span style={{ fontWeight: "700", color: "#64748b" }}>Level {c.level}</span>
            ) },
            { key: "commission", label: "COMMISSION", render: (c) => (
              <span style={{ ...S.commissionVal, fontSize: "14px" }}>{fmtPct(c.commission_percentage)}</span>
            ) },
            { key: "legs", label: "LEGS", render: (c) => (
              <span style={{ ...S.legCount, fontSize: "14px" }}>{c.min_legs} Legs</span>
            ) },
            { key: "range", label: "RANGE (MIN - MAX)", align: "right", render: (c) => (
              <span style={{ fontWeight: "700", color: "#10b981" }}>
                {fmtRange(c.min_range)} - {fmtRange(c.max_range)}
              </span>
            ) },
          ]}
        />
      </div>

      <div style={{ marginTop: "48px" }}>
        <h2 style={{ ...S.title, marginBottom: "20px" }}>Travel Rewards</h2>
        <RewardsTable
          rows={travelCommissions}
          emptyLabel="No travel rewards configuration found."
          columns={rewardColumns}
        />
      </div>

      <div style={{ marginTop: "48px" }}>
        <h2 style={{ ...S.title, marginBottom: "20px" }}>Car Rewards</h2>
        <RewardsTable
          rows={carCommissions}
          emptyLabel="No car rewards configuration found."
          columns={rewardColumns}
        />
      </div>

      <div style={{ marginTop: "48px" }}>
        <h2 style={{ ...S.title, marginBottom: "20px" }}>House Rewards</h2>
        <RewardsTable
          rows={houseCommissions}
          emptyLabel="No house rewards configuration found."
          columns={rewardColumns}
        />
      </div>

      <div style={{ marginTop: "48px" }}>
        <h2 style={{ ...S.title, marginBottom: "20px" }}>Lifestyle Rewards</h2>
        <RewardsTable
          rows={lifestyleCommissions}
          emptyLabel="No lifestyle rewards configuration found."
          columns={rewardColumns}
        />
      </div>
    </main>
  );
}

const S = {
  root: { width: "100%", margin: "0", animation: "f-in 0.6s cubic-bezier(0.16, 1, 0.3, 1)" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" },
  title: { fontSize: "26px", fontWeight: "600", color: "#0f172a", margin: 0, letterSpacing: "-0.5px" },
  subtitle: { fontSize: "15px", color: "#64748b", marginTop: "6px", fontWeight: "400" },
  headerActions: { },
  badgeCount: { background: "#eee", color: "#64748b", padding: "6px 14px", borderRadius: "10px", fontSize: "13px", fontWeight: "700" },

  error: { background: "#fef2f2", color: "#dc2626", padding: "16px", borderRadius: "12px", marginBottom: "24px", fontSize: "14px", border: "1px solid #fecaca" },

  listContainer: { },
  tableCard: {
    background: "#fff",
    borderRadius: "24px",
    border: "1px solid #f1f5f9",
    overflow: "hidden",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)"
  },
  table: { width: "100%", borderCollapse: "collapse" },
  theadTr: { background: "#f8fafc" },
  th: {
    textAlign: "left",
    padding: "16px 24px",
    fontSize: "11px",
    fontWeight: "800",
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "1px"
  },
  tr: { borderBottom: "1px solid #f8fafc", transition: "all 0.2s" },
  td: { padding: "20px 24px" },

  rankInfo: { display: "flex", alignItems: "center", gap: "16px" },
  rankIcon: {
    width: "36px", height: "36px", borderRadius: "10px",
    background: "linear-gradient(135deg, #6366f1, #4f46e5)",
    color: "#fff", fontSize: "13px", fontWeight: "800",
    display: "flex", alignItems: "center", justifyContent: "center"
  },
  rankName: { fontSize: "16px", fontWeight: "700", color: "#1e293b" },

  commissionBadge: { display: "flex", flexDirection: "column" },
  commissionVal: { fontSize: "15px", fontWeight: "800", color: "#6366f1" },
  commissionLabel: { fontSize: "10px", color: "#94a3b8", fontWeight: "600", textTransform: "uppercase" },

  structureInfo: { display: "flex", flexDirection: "column" },
  legCount: { fontSize: "15px", fontWeight: "700", color: "#334155" },
  legLabel: { fontSize: "11px", color: "#94a3b8" },

  budgetInfo: { display: "flex", flexDirection: "column" },
  budgetVal: { fontSize: "16px", fontWeight: "800", color: "#10b981" },
};

const GLOBAL_CSS = `
@keyframes f-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
tr[style*="transition: all"]:hover {
  background: #fdfdff !important;
  transform: scale(1.002);
  z-index: 10;
}

/* Default: table view (desktop/tablet), card list hidden */
.rt-card-list { display: none; }

@media (max-width: 720px) {
  .dp-main { padding: 12px 12px 60px !important; }

  /* Hide the dense multi-column table on small screens */
  .rt-table-wrap { display: none; }

  /* Stacked card list instead */
  .rt-card-list { display: block; }
  .rt-card {
    padding: 14px 16px;
    border-bottom: 1px solid #f8fafc;
  }
  .rt-card:last-child { border-bottom: none; }
  .rt-card-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 5px 0;
  }
  .rt-card-label {
    font-size: 10px;
    font-weight: 800;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
  }
  .rt-card-value {
    text-align: right;
    min-width: 0;
  }
}
`;